import { useNavigate } from 'react-router-dom';
import {
  BarChart3, Building2, DollarSign, Package, Wrench,
  ChevronLeft, ChevronRight,
} from 'lucide-react';

// ── Hub definitions ──────────────────────────────────────────────────────────

interface IomiHubDef {
  id: string;
  label: string;
  desc: string;
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
  path: string;
}

const HUBS: IomiHubDef[] = [
  {
    id: 'dashboard',
    label: 'Platform Dashboard',
    desc: 'Platform-wide health, usage, growth metrics, and PDF generation tracking.',
    icon: BarChart3,
    path: '/admin/platform-tools/dashboard',
  },
  {
    id: 'orgs-users',
    label: 'Organizations & Users',
    desc: 'Manage client companies and platform users.',
    icon: Building2,
    path: '/admin/platform-tools/orgs-users',
  },
  {
    id: 'billing',
    label: 'Billing & Pricing',
    desc: 'Stripe products, prices, and coupons.',
    icon: DollarSign,
    path: '/admin/platform-tools/billing',
  },
  {
    id: 'defaults',
    label: 'Platform Defaults',
    desc: 'Master inventory, services, and residential config.',
    icon: Package,
    path: '/admin/platform-tools/defaults',
  },
  {
    id: 'system',
    label: 'System & Developer',
    desc: 'App versions, releases, and developer tools.',
    icon: Wrench,
    path: '/admin/platform-tools/system',
  },
];

// ── Hub card ─────────────────────────────────────────────────────────────────

function HubCard({ hub, onClick }: { hub: IomiHubDef; onClick: () => void }) {
  const Icon = hub.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-[var(--color-surface)] border border-[var(--color-border)]
                 rounded-xl p-4 shadow-brand-sm
                 flex items-center gap-4
                 md:flex-col md:items-center md:text-center md:gap-3 md:p-6 md:min-h-[140px] md:justify-center
                 hover:border-plum/40 hover:shadow-brand-md active:bg-mist
                 transition-all duration-150"
    >
      <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center
                      md:w-12 md:h-12">
        <Icon size={20} className="text-plum" />
      </div>
      <div className="flex-1 min-w-0 md:flex-none md:w-full">
        <p className="font-semibold text-small text-[var(--color-text-primary)]">{hub.label}</p>
        <p className="text-[13px] text-[var(--color-text-secondary)] mt-0.5 leading-snug">{hub.desc}</p>
      </div>
      <ChevronRight size={18} className="shrink-0 text-slate md:hidden" />
    </button>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function IomiAdminToolsPage() {
  const navigate = useNavigate();

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-4xl md:mx-auto">
      <button
        type="button"
        onClick={() => navigate('/admin')}
        className="flex items-center gap-1 text-small text-slate hover:text-plum mb-4 -ml-1
                   transition-colors"
      >
        <ChevronLeft size={16} />
        Admin Panel
      </button>

      <h1 className="font-display text-h1 text-[var(--color-text-primary)] mb-1">Platform Admin Tools</h1>
      <p className="text-small text-[var(--color-text-secondary)] mb-6">Platform-level management tools.</p>

      <div className="flex flex-col gap-3 md:grid md:grid-cols-2 md:gap-4">
        {HUBS.map(hub => (
          <HubCard key={hub.id} hub={hub} onClick={() => navigate(hub.path)} />
        ))}
      </div>
    </div>
  );
}
